import axios, { type AxiosRequestConfig } from 'axios';

import type { CreativeDraft } from './custom-formats';

/**
 * Saved Custom-studio drafts, persisted in the spark-creatives service (owner-scoped) so an owner
 * can create a custom creative, save it, and re-open it to edit later. The `draft` is the studio's
 * own document (format is stored separately as `format_id`).
 */

export interface SavedCustom {
  id: string;
  tenant_id: string;
  name: string;
  format_id: string;
  draft: CreativeDraft;
  thumbnail_url?: string;
  created_at?: string;
  updated_at?: string;
}

const creativesBase = (): string => {
  const cfg = window.config as unknown as Record<string, unknown>;
  return typeof cfg?.creativesApiUrl === 'string' ? (cfg.creativesApiUrl as string).replace(/\/+$/, '') : '';
};

const authConfig = (signal?: AbortSignal): AxiosRequestConfig => {
  const token = localStorage.getItem('access_token') || '';
  return { signal, _owsNoAuthRecovery: true, ...(token ? { headers: { Authorization: token } } : {}) };
};

/** Whether the custom-drafts backend is reachable (the service URL is configured). */
export const customApiConfigured = (): boolean => creativesBase() !== '';

/** List the owner's saved custom drafts, newest first. Returns [] when unconfigured/unreachable. */
export const listCustom = async (signal?: AbortSignal): Promise<SavedCustom[]> => {
  const base = creativesBase();
  if (!base) return [];
  const res = await axios.get<{ data?: SavedCustom[] }>(`${base}/custom/list`, authConfig(signal));
  return Array.isArray(res.data?.data) ? res.data.data : [];
};

/** Create or update a draft (upsert by id). Returns the saved id. */
export const saveCustom = async (
  input: {
    id?: string;
    tenantId?: number;
    name: string;
    formatId: string;
    draft: CreativeDraft;
    thumbnailUrl?: string;
  },
  signal?: AbortSignal,
): Promise<string> => {
  const base = creativesBase();
  if (!base) throw new Error('creatives service not configured');
  const res = await axios.post<{ ok?: boolean; id?: string }>(
    `${base}/custom`,
    {
      id: input.id,
      tenant_id: input.tenantId != null ? String(input.tenantId) : '',
      name: input.name,
      format_id: input.formatId,
      draft: input.draft,
      thumbnail_url: input.thumbnailUrl ?? '',
    },
    authConfig(signal),
  );
  return res.data?.id ?? input.id ?? '';
};

/** Delete one of the owner's drafts. */
export const deleteCustom = async (id: string, signal?: AbortSignal): Promise<void> => {
  const base = creativesBase();
  if (!base) return;
  await axios.delete(`${base}/custom/${encodeURIComponent(id)}`, authConfig(signal));
};
